import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { apiClient } from '@copro/api';
import { keys as parentKeys } from '@copro/api';
import { useAuth } from '../hooks/useAuth';
import { SpaceContext, type Space, type SpaceMembership } from './SpaceContext';

const ACTIVE_SPACE_KEY = 'copro-active-space-slug';

type SpaceContextType = import('./SpaceContext').SpaceContextType;

type MembershipsResult = {
  memberships: SpaceMembership[];
  isSuperAdmin: boolean;
};

async function fetchMembershipsForUser(userId: string): Promise<MembershipsResult> {
  const { data: profile } = await apiClient
    .from('profiles')
    .select('is_super_admin')
    .eq('id', userId)
    .single();

  const isSuperAdmin = profile?.is_super_admin ?? false;

  if (isSuperAdmin) {
    const { data: spaces } = await apiClient
      .from('spaces')
      .select('id, name, slug')
      .order('name');

    const memberships: SpaceMembership[] = (spaces ?? []).map((s) => ({
      space: s as Space,
      role: 'admin' as const,
    }));

    return { memberships, isSuperAdmin };
  }

  const { data: members } = await apiClient
    .from('space_members')
    .select('role, spaces (id, name, slug)')
    .eq('user_id', userId);

  const memberships: SpaceMembership[] = (members ?? [])
    .filter((m) => m.spaces)
    .map((m) => ({
      space: m.spaces as unknown as Space,
      role: m.role as SpaceMembership['role'],
    }));

  return { memberships, isSuperAdmin: false };
}

export function SpaceProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [activeSlug, setActiveSlug] = useState<string | null>(
    () => localStorage.getItem(ACTIVE_SPACE_KEY)
  );

  const enabled = !authLoading && !!user?.id;

  const { data, isPending, fetchStatus } = useQuery({
    queryKey: [...parentKeys.all, 'spaceProvider', 'memberships', user?.id ?? ''] as const,
    queryFn: () => fetchMembershipsForUser(user!.id),
    enabled,
  });

  const memberships = data?.memberships ?? [];
  const isSuperAdmin = data?.isSuperAdmin ?? false;
  const isLoading = enabled
    ? isPending && fetchStatus !== 'idle'
    : authLoading;

  const switchSpace = useCallback((slug: string) => {
    setActiveSlug(slug);
    localStorage.setItem(ACTIVE_SPACE_KEY, slug);
  }, []);

  const currentSpace = useMemo(() => {
    if (memberships.length === 0) return null;

    const match = memberships.find((m) => m.space.slug === activeSlug);
    if (match) return match.space;

    const fallback = memberships[0].space;
    localStorage.setItem(ACTIVE_SPACE_KEY, fallback.slug);
    setActiveSlug(fallback.slug);
    return fallback;
  }, [memberships, activeSlug]);

  const contextValue = useMemo<SpaceContextType>(() => ({
    currentSpace,
    memberships,
    isSuperAdmin,
    isLoading,
    switchSpace,
  }), [currentSpace, memberships, isSuperAdmin, isLoading, switchSpace]);

  return (
    <SpaceContext.Provider value={contextValue}>
      {children}
    </SpaceContext.Provider>
  );
}
